import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import clsx from "clsx";
import { Api, netflixToLibraryItem, curiosityToLibraryItem, type LibraryItem } from "../lib/api.js";
import { MediaStrip } from "../components/MediaStrip.js";
import { BookmarkButton } from "../components/BookmarkButton.js";
import { linkForItem } from "../lib/linkForItem.js";

const KIND_LABELS: { value: string; label: string }[] = [
  { value: "",              label: "All" },
  { value: "youtube_video", label: "YouTube" },
  { value: "live_channel",  label: "Live" },
  { value: "vod",           label: "Movies" },
  { value: "tv_show",       label: "TV Shows" },
];

const PAGE_SIZE = 60;

// Year dropdown range — top at the current year, oldest at 1900. No future
// years (the catalog can't contain content released later than today).
const YEAR_OPTIONS = (() => {
  const currentYear = new Date().getFullYear();
  const ys: number[] = [];
  for (let y = currentYear; y >= 1900; y--) ys.push(y);
  return ys;
})();

export function Library(): JSX.Element {
  // All filter state lives in the URL — back-button restores everything,
  // and the search box stays populated when the user navigates to a result
  // and back. Local mirror state is just to keep typing responsive (we
  // debounce writes to the URL).
  const [params, setParams] = useSearchParams();
  const kind = params.get("kind") ?? "";
  const urlQ = params.get("q") ?? "";
  const offset = Math.max(0, Number(params.get("offset") ?? 0));
  const sourceId = params.get("source_id") ? Number(params.get("source_id")) : null;
  // External service filter — searches a service catalog directly instead of
  // the local IPTV mirror (curiosity = local nightly mirror, plays direct;
  // netflix = TMDB search, plays via Real-Debrid). Mutually exclusive with
  // source_id; lives in the URL like every other filter.
  const extSourceRaw = params.get("ext");
  const extSource: "curiosity" | "netflix" | null =
    extSourceRaw === "curiosity" || extSourceRaw === "netflix" ? extSourceRaw : null;
  const extLabel = extSource === "curiosity" ? "CuriosityStream" : "Netflix";
  const yearFrom = params.get("year_from") ? Number(params.get("year_from")) : null;
  const yearTo = params.get("year_to") ? Number(params.get("year_to")) : null;
  // "Best source only" mode hides external (Real-Debrid) hits when the local
  // catalog already has the same item or any hits at all. Off-by-default so
  // returning visitors who set "show alternates" keep their preference.
  const showAlternates = params.get("alt") === "1";

  // Input is mirrored locally so each keystroke doesn't push a new URL entry.
  // We push to URL after a debounce.
  const [q, setQ] = useState(urlQ);
  const debouncedQ = useDebounced(q, 250);
  const lastPushedRef = useRef<string>(urlQ);
  useEffect(() => {
    if (debouncedQ === lastPushedRef.current) return;
    lastPushedRef.current = debouncedQ;
    setParams((prev) => {
      if (debouncedQ.length > 0) prev.set("q", debouncedQ); else prev.delete("q");
      prev.delete("offset"); // page resets on new query
      return prev;
    }, { replace: true });
  }, [debouncedQ, setParams]);

  // If the URL changes externally (e.g. browser back), pull it into the input.
  useEffect(() => {
    if (urlQ !== q && urlQ !== lastPushedRef.current) {
      setQ(urlQ);
      lastPushedRef.current = urlQ;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQ]);

  function setKind(value: string): void {
    setParams((prev) => {
      if (value) prev.set("kind", value); else prev.delete("kind");
      prev.delete("offset");
      return prev;
    });
  }
  function setSourceId(value: number | null): void {
    setParams((prev) => {
      if (value !== null) prev.set("source_id", String(value)); else prev.delete("source_id");
      prev.delete("ext"); // local and external source filters are exclusive
      prev.delete("offset");
      return prev;
    });
  }
  function setExtSource(value: "curiosity" | "netflix" | null): void {
    setParams((prev) => {
      if (value !== null) prev.set("ext", value); else prev.delete("ext");
      prev.delete("source_id");
      prev.delete("offset");
      return prev;
    });
  }
  function setYearFrom(value: number | null): void {
    setParams((prev) => {
      if (value !== null) prev.set("year_from", String(value)); else prev.delete("year_from");
      prev.delete("offset");
      return prev;
    });
  }
  function setYearTo(value: number | null): void {
    setParams((prev) => {
      if (value !== null) prev.set("year_to", String(value)); else prev.delete("year_to");
      prev.delete("offset");
      return prev;
    });
  }
  function setOffset(updater: number | ((prev: number) => number)): void {
    setParams((prev) => {
      const cur = Math.max(0, Number(prev.get("offset") ?? 0));
      const next = typeof updater === "function" ? updater(cur) : updater;
      if (next <= 0) prev.delete("offset"); else prev.set("offset", String(next));
      return prev;
    });
  }
  function setShowAlternates(on: boolean): void {
    setParams((prev) => {
      if (on) prev.set("alt", "1"); else prev.delete("alt");
      return prev;
    });
  }

  const sourcesQ = useQuery({ queryKey: ["sources"], queryFn: Api.sources.list });

  const seriesMode = kind === "tv_show";

  const libQ = useQuery({
    queryKey: ["library", { kind, q: debouncedQ, sourceId, yearFrom, yearTo, offset }],
    queryFn: () => Api.library.list({
      kind: kind || undefined,
      q: debouncedQ || undefined,
      source_id: sourceId ?? undefined,
      year_from: yearFrom ?? undefined,
      year_to: yearTo ?? undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    enabled: !seriesMode && extSource === null,
  });

  // External service search — the active result set when a service is picked
  // in the Source filter. With no query it shows the service's featured /
  // trending list, so the filter doubles as a mini-browse.
  const extQ = useQuery({
    queryKey: ["ext-source", extSource, debouncedQ],
    queryFn: async (): Promise<LibraryItem[]> => {
      if (extSource === "curiosity") {
        const res = debouncedQ.length > 2
          ? await Api.library.searchCuriosity(debouncedQ)
          : await Api.library.curiosityFeatured();
        return res.items.map(curiosityToLibraryItem);
      }
      const res = debouncedQ.length > 2
        ? await Api.library.searchNetflix(debouncedQ)
        : await Api.library.netflixTrending();
      return res.items.map(netflixToLibraryItem);
    },
    enabled: extSource !== null,
    staleTime: 60_000,
  });

  const seriesQ = useQuery({
    queryKey: ["series-list", { q: debouncedQ, sourceId, yearFrom, yearTo, offset }],
    queryFn: () => Api.library.listSeries({
      q: debouncedQ || undefined,
      source_id: sourceId ?? undefined,
      year_from: yearFrom ?? undefined,
      year_to: yearTo ?? undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    enabled: seriesMode && extSource === null,
  });

  const total = seriesMode ? (seriesQ.data?.total ?? 0) : (libQ.data?.total ?? 0);
  const mode = libQ.data?.mode ?? "browse";
  const items = libQ.data?.items ?? [];
  const seriesItems = seriesQ.data?.items ?? [];
  const isLoading = seriesMode ? seriesQ.isLoading : libQ.isLoading;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);

  // Both fallbacks gate on "the currently-active local query has finished
  // fetching" — without this, switching to TV Shows would keep firing the
  // YouTube fallback because libQ.isFetched stays true even when disabled.
  const fetchedAny = seriesMode ? seriesQ.isFetched : libQ.isFetched;
  const sparse = total < 6;

  // YouTube fallback — only meaningful for general/topical searches. When
  // user explicitly picks Movies or TV Shows, YouTube clips aren't what they
  // want; Real-Debrid is the right fallback for those.
  const ytFallback = useQuery({
    queryKey: ["library-yt", debouncedQ],
    queryFn: () => Api.library.searchYouTube(debouncedQ),
    enabled:
      extSource === null &&
      debouncedQ.length > 2 && fetchedAny && sparse &&
      kind !== "tv_show" && kind !== "vod",
    staleTime: 60_000,
  });

  // Catalog fallback — discovery layer via TMDB (free); titles play via
  // Real-Debrid by IMDb id. Gated on sparse local results; cached 1h server-side.
  const netflixFallback = useQuery({
    queryKey: ["library-netflix", debouncedQ],
    queryFn: () => Api.library.searchNetflix(debouncedQ),
    enabled:
      extSource === null &&
      debouncedQ.length > 2 && fetchedAny && sparse &&
      kind !== "live_channel" && kind !== "youtube_video",
    staleTime: 60 * 60_000,
  });

  // CuriosityStream fallback — same sparse-results gate, but these play
  // DIRECTLY (Douglas's subscription; DRM-free HLS), no Real-Debrid needed.
  const curiosityFallback = useQuery({
    queryKey: ["library-curiosity", debouncedQ],
    queryFn: () => Api.library.searchCuriosity(debouncedQ),
    enabled:
      extSource === null &&
      debouncedQ.length > 2 && fetchedAny && sparse &&
      kind !== "live_channel" && kind !== "youtube_video",
    staleTime: 60 * 60_000,
  });

  // Real-Debrid fallback — looks the query up via TMDB → IMDb → Torrentio
  // → RD. When the user has no kind filter, the backend auto-detects series
  // vs movie. Skip for Live and YouTube kinds (RD doesn't apply there).
  const rdKind: "movie" | "series" | undefined =
    kind === "tv_show" ? "series" : kind === "vod" ? "movie" : undefined;
  const rdFallback = useQuery({
    queryKey: ["library-rd", debouncedQ, rdKind ?? "auto"],
    queryFn: () => Api.library.searchDebrid(debouncedQ, undefined, rdKind),
    // When the viewer has explicitly picked a *local* source filter, they
    // want to stay inside that source — don't surface external RD picks.
    enabled:
      extSource === null &&
      debouncedQ.length > 2 && fetchedAny && sparse &&
      kind !== "live_channel" && kind !== "youtube_video" &&
      sourceId === null &&
      // Skip when "best source only" is on AND local has any hits.
      (showAlternates || total === 0),
    staleTime: 60_000,
  });

  // Multi-result Real-Debrid live search — surfaces multiple TMDB candidates
  // alongside the local hits, so a query like "Real Time with Bill Maher"
  // returns every Maher show TMDB knows about, each clickable to resolve via
  // Torrentio at watch time.
  const rdMulti = useQuery({
    queryKey: ["library-rd-multi", debouncedQ, kind, sourceId, showAlternates],
    queryFn: () => Api.libraryWithDebrid({
      q: debouncedQ,
      kind: kind || undefined,
      year_from: yearFrom ?? undefined,
      year_to: yearTo ?? undefined,
      limit: 12,
    }),
    // Suppress when the viewer's narrowed to a specific local source — they
    // don't want RD picks bleeding into "Premium Club Movies only".
    // Also suppressed when "best source only" is on and local has hits.
    enabled:
      extSource === null &&
      debouncedQ.length > 2 &&
      kind !== "live_channel" && kind !== "youtube_video" &&
      sourceId === null &&
      (showAlternates || total === 0),
    staleTime: 60_000,
  });

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl text-ivory">Search</h1>
        <span className="section-label">
          {extSource
            ? (extQ.isLoading ? "…" : `${(extQ.data?.length ?? 0).toLocaleString()} on ${extLabel}`)
            : libQ.isLoading ? "…" : `${total.toLocaleString()} items`}
        </span>
      </div>
      <div className="ornament-rule" />

      <div className="card p-4 space-y-3 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[280px] relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-parchment" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search… (e.g. mash, beef wellington, chernobyl)"
              className="w-full pl-9 pr-3 py-2 bg-ink/60 border border-hairline rounded-md text-ivory focus:border-gold outline-none"
            />
          </div>

          {extSource === null && (
            <div className="flex gap-1">
              {KIND_LABELS.map((k) => (
                <button
                  key={k.value}
                  onClick={() => setKind(k.value)}
                  aria-pressed={kind === k.value}
                  className={clsx(
                    "px-3 py-2 rounded-md text-sm border transition-colors",
                    kind === k.value
                      ? "bg-vermilion border-goldHi text-ivory"
                      : "border-hairline text-parchment hover:text-ivory hover:bg-elev",
                  )}
                >
                  {k.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 text-sm">
          {extSource === null && (
            <>
              <span className="section-label">Year</span>
              <div className="flex items-center gap-2">
                <select
                  value={yearFrom ?? ""}
                  onChange={(e) => setYearFrom(e.target.value ? Number(e.target.value) : null)}
                  className="bg-ink/60 border border-hairline rounded-md px-3 py-1.5 text-ivory focus:border-gold outline-none tabular-nums"
                >
                  <option value="">From</option>
                  {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
                <span className="text-parchment">—</span>
                <select
                  value={yearTo ?? ""}
                  onChange={(e) => setYearTo(e.target.value ? Number(e.target.value) : null)}
                  className="bg-ink/60 border border-hairline rounded-md px-3 py-1.5 text-ivory focus:border-gold outline-none tabular-nums"
                >
                  <option value="">To</option>
                  {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
                {(yearFrom !== null || yearTo !== null) && (
                  <button
                    onClick={() => { setYearFrom(null); setYearTo(null); }}
                    className="pill-ghost px-2 py-1 text-xs"
                  >
                    Clear
                  </button>
                )}
              </div>
            </>
          )}

          <span className="section-label ml-4">Source</span>
          <select
            value={extSource ? `ext:${extSource}` : (sourceId ?? "")}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "ext:curiosity" || v === "ext:netflix") setExtSource(v.slice(4) as "curiosity" | "netflix");
              else setSourceId(v ? Number(v) : null);
            }}
            className="bg-ink/60 border border-hairline rounded-md px-3 py-1.5 text-ivory focus:border-gold outline-none text-sm"
          >
            <option value="">All sources</option>
            <option value="ext:curiosity">CuriosityStream</option>
            <option value="ext:netflix">Netflix (via TMDB)</option>
            {sourcesQ.data?.sources.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>

          <span className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowAlternates(!showAlternates)}
              aria-pressed={showAlternates}
              className={clsx(
                "px-3 py-1.5 rounded-md text-xs border transition-colors font-display tracking-smallcaps",
                showAlternates
                  ? "bg-vermilion border-goldHi text-ivory"
                  : "border-hairline text-parchment hover:text-ivory hover:bg-elev",
              )}
              title={showAlternates
                ? "Showing every source we can resolve — click to collapse"
                : "Showing Premium Club hits only; click to also include Real-Debrid"}
            >
              {showAlternates ? "All sources" : "Best source only"}
            </button>
          </span>
        </div>
      </div>

      {/* External service result set (CuriosityStream / Netflix picked in the
          Source filter) — replaces the local grid, fallbacks, and pager. */}
      {extSource !== null && (
        extQ.isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {Array.from({ length: 12 }).map((_, i) => <div key={i} className="skeleton aspect-video" />)}
          </div>
        ) : (extQ.data?.length ?? 0) === 0 ? (
          <div className="card p-10 text-center">
            <div className="font-display tracking-smallcaps text-parchment">
              {debouncedQ.length > 2
                ? <>No {extLabel} results for “{debouncedQ}”.</>
                : <>Nothing featured on {extLabel} right now.</>}
            </div>
            {extSource === "netflix" && (
              <div className="text-parchment/70 text-sm mt-2">
                Titles found here stream via Real-Debrid, not from Netflix itself.
              </div>
            )}
          </div>
        ) : (
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {extQ.data!.map((it) => <LibraryCard key={it.id} item={it} />)}
          </ul>
        )
      )}

      {extSource === null && (isLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {Array.from({ length: 12 }).map((_, i) => <div key={i} className="skeleton aspect-video" />)}
        </div>
      ) : (seriesMode ? seriesItems.length === 0 : items.length === 0) ? (
        <div className="card p-10 text-center">
          <div className="font-display tracking-smallcaps text-parchment">
            Not in your IPTV catalog.
          </div>
          {debouncedQ && (
            <div className="text-parchment/70 text-sm mt-2 max-w-[60ch] mx-auto">
              The Premium Club playlists cap at ~5,000 TV episodes + ~18,000 movies +
              live channels. If "{debouncedQ}" isn't here, your provider doesn't list it.
            </div>
          )}
        </div>
      ) : seriesMode ? (
        <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {seriesItems.map((s) => <SeriesCard key={s.series_id} series={s} />)}
        </ul>
      ) : (
        <>
          {mode === "fuzzy" && (
            <div className="text-parchment/60 text-xs mb-3 italic">
              No exact matches — showing the closest titles instead.
            </div>
          )}
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {items.map((it) => <LibraryCard key={it.id} item={it} />)}
          </ul>
        </>
      ))}

      {/* Real-Debrid multi-candidate row — TMDB titles that aren't in your
          catalog but can be resolved via Torrentio at click time. */}
      {extSource === null && debouncedQ.length > 2 && (rdMulti.data?.debrid.items.length ?? 0) > 0 && (
        <section className="mt-10">
          <div className="ornament-rule" />
          <div className="flex items-baseline justify-between mb-3">
            <div className="section-label">Also via Real-Debrid</div>
            <span className="text-parchment text-xs">
              {rdMulti.data!.debrid.items.length} resolvable via Torrentio
            </span>
          </div>
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {rdMulti.data!.debrid.items.map((it) => <LibraryCard key={it.id} item={it} />)}
          </ul>
        </section>
      )}

      {/* Real-Debrid single-resolve fallback (kept for cases the multi-search
          misses but the single-resolver finds — e.g. exact-title hits). */}
      {extSource === null && debouncedQ.length > 2 && (rdFallback.data?.streams.length ?? 0) > 0 && (
        <DebridFallbackSection
          query={debouncedQ}
          resolved={rdFallback.data!.resolved}
          streams={rdFallback.data!.streams}
          kind={rdKind ?? "movie"}
        />
      )}

      {/* YouTube fallback when local results are sparse */}
      {extSource === null && debouncedQ.length > 2 && (ytFallback.data?.items.length ?? 0) > 0 && (
        <section className="mt-12">
          <div className="ornament-rule" />
          <div className="flex items-baseline justify-between mb-3">
            <div className="section-label">Also on YouTube</div>
            <span className="text-parchment text-xs">
              {ytFallback.data!.items.length} hits via your API key
            </span>
          </div>
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {ytFallback.data!.items.map((it) => <YouTubeFallbackCard key={it.video_id} item={it} />)}
          </ul>
        </section>
      )}

      {/* Catalog fallback — discovery; titles play via Real-Debrid */}
      {extSource === null && debouncedQ.length > 2 && (netflixFallback.data?.items.length ?? 0) > 0 && (
        <section className="mt-12">
          <div className="ornament-rule" />
          <MediaStrip
            label="More to watch"
            items={netflixFallback.data!.items.map(netflixToLibraryItem)}
            variant="poster"
            caption={`${netflixFallback.data!.items.length} found via TMDB · stream via Real-Debrid`}
          />
        </section>
      )}

      {/* CuriosityStream fallback — plays directly with Douglas's account */}
      {extSource === null && debouncedQ.length > 2 && (curiosityFallback.data?.items.length ?? 0) > 0 && (
        <section className="mt-12">
          <div className="ornament-rule" />
          <MediaStrip
            label="On CuriosityStream"
            items={curiosityFallback.data!.items.map(curiosityToLibraryItem)}
            variant="poster"
            caption={`${curiosityFallback.data!.items.length} documentaries · plays with your CuriosityStream account`}
          />
        </section>
      )}

      {extSource === null && total > PAGE_SIZE && (
        <div className="mt-8 flex items-center justify-between">
          <span className="text-parchment text-sm tabular-nums">
            {(offset + 1).toLocaleString()} – {pageEnd.toLocaleString()} of {total.toLocaleString()}
          </span>
          <div className="flex gap-2">
            <button
              className="pill-ghost"
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
              disabled={offset === 0}
            >
              ← Prev
            </button>
            <button
              className="pill-ghost"
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [d, setD] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setD(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return d;
}

function LibraryCard({ item }: { item: LibraryItem & { via?: string } }): JSX.Element {
  const badge = useMemo(() => kindBadge(item.kind), [item.kind]);
  const isRd = item.id.startsWith("rd:") || item.via === "real-debrid";
  return (
    <li>
      <Link
        to={linkForItem({ id: item.id, kind: item.kind, category: item.category })}
        className="card group block overflow-hidden hover:scale-[1.02] transition-transform duration-200 ease-spring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-goldHi"
      >
        <div className="aspect-video bg-elev relative">
          {item.thumbnail_url ? (
            <img
              src={item.thumbnail_url}
              alt=""
              loading="lazy"
              referrerPolicy="no-referrer"
              className="w-full h-full object-cover"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-display text-parchment/40 text-3xl">
              {item.title.slice(0, 2).toUpperCase()}
            </div>
          )}
          <BookmarkButton itemId={item.id} />
          <span
            className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-smallcaps font-display"
            style={{ background: "rgba(15,6,6,0.78)", color: badge.color, border: `1px solid ${badge.color}55` }}
          >
            {badge.label}
          </span>
          {isRd && (
            <span
              className="absolute top-9 right-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-smallcaps font-display bg-ink/85 border"
              style={{ color: "#9DD9C8", borderColor: "#9DD9C855" }}
              title="Resolved on demand via Real-Debrid / Premiumize / AllDebrid"
            >
              via RD
            </span>
          )}
          {item.duration_s !== null && item.duration_s > 0 && (
            <span className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded text-[11px] bg-ink/80 text-ivory tabular-nums">
              {formatDuration(item.duration_s)}
            </span>
          )}
          {typeof item.tmdb_rating === "number" && item.tmdb_rating > 0 && (
            <span className="absolute bottom-2 left-2 px-1.5 py-0.5 rounded text-[11px] bg-ink/80 text-goldHi tabular-nums font-display">
              ★ {item.tmdb_rating.toFixed(1)}
            </span>
          )}
        </div>
        <div className="p-3">
          <div className="text-ivory text-sm leading-tight line-clamp-2 min-h-[2.5em]">{item.title}</div>
          <div className="text-parchment text-xs mt-1 truncate">
            {item.tmdb_genres && item.tmdb_genres.length > 0
              ? item.tmdb_genres.slice(0, 3).join(" · ")
              : item.creator_name ?? item.category ?? "—"}
          </div>
        </div>
      </Link>
    </li>
  );
}

function SeriesCard({
  series,
}: {
  series: { series_id: string; title: string; thumbnail_url: string | null; year: number | null; episode_count: number };
}): JSX.Element {
  return (
    <li>
      <Link
        to={`/series/${encodeURIComponent(series.series_id)}`}
        className="card group block overflow-hidden hover:scale-[1.02] transition-transform duration-200 ease-spring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-goldHi"
      >
        <div className="aspect-video bg-elev relative">
          {series.thumbnail_url ? (
            <img
              src={series.thumbnail_url}
              alt=""
              loading="lazy"
              referrerPolicy="no-referrer"
              className="w-full h-full object-cover"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-display text-parchment/40 text-3xl">
              {series.title.slice(0, 2).toUpperCase()}
            </div>
          )}
          <span
            className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-smallcaps font-display"
            style={{ background: "rgba(15,6,6,0.78)", color: "#C42D3A", border: "1px solid #C42D3A55" }}
          >
            TV · {series.episode_count} ep{series.episode_count === 1 ? "" : "s"}
          </span>
        </div>
        <div className="p-3">
          <div className="text-ivory text-sm leading-tight line-clamp-2 min-h-[2.5em]">{series.title}</div>
          {series.year !== null && (
            <div className="text-parchment text-xs mt-1 tabular-nums">{series.year}</div>
          )}
        </div>
      </Link>
    </li>
  );
}

function DebridFallbackSection({
  query, resolved, streams, kind,
}: {
  query: string;
  resolved: { tmdb_id: number; imdb_id: string; title: string; year: string | null; poster_url: string | null } | null;
  streams: { display_title: string; quality: string; hdr: boolean; codec: string | null; size_label: string | null; seeders: number | null; source: string | null; url: string; info_hash?: string | null; file_idx?: number | null; filename?: string | null }[];
  kind: "movie" | "series";
}): JSX.Element {
  // Minting a link is a round trip to the debrid service, so the row that was
  // clicked says so rather than looking dead for a second.
  const [minting, setMinting] = useState<string | null>(null);
  const [mintError, setMintError] = useState<string | null>(null);
  return (
    <section className="mt-12">
      <div className="ornament-rule" />
      <div className="flex items-baseline justify-between mb-3">
        <div className="section-label">Available via Real-Debrid</div>
        <span className="text-parchment text-xs">
          {resolved ? `${resolved.title}${resolved.year ? ` (${resolved.year})` : ""} · ${streams.length} streams` : `${streams.length} streams`}
        </span>
      </div>
      {mintError && <p className="text-vermilionHi text-xs mb-2">{mintError}</p>}
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {resolved?.poster_url && (
          <img
            src={resolved.poster_url}
            alt={resolved.title}
            className="rounded-lg w-full max-w-[260px] aspect-[2/3] object-cover"
            referrerPolicy="no-referrer"
          />
        )}
        <ul className="space-y-2">
          {streams.slice(0, 12).map((s, i) => {
            const externalUrl = (url: string): string =>
              `/watch/external?url=${encodeURIComponent(url)}` +
              `&title=${encodeURIComponent(s.display_title)}` +
              (resolved?.poster_url ? `&poster=${encodeURIComponent(resolved.poster_url)}` : "") +
              (resolved?.imdb_id ? `&imdb=${encodeURIComponent(resolved.imdb_id)}&mk=${kind}` : "");
            // A self-resolved candidate has no URL until it's minted, so those
            // rows resolve on click instead of being a plain link.
            const needsMint = !s.url && !!s.info_hash;
            return (
              <li key={i}>
                <Link
                  to={s.url ? externalUrl(s.url) : "#"}
                  onClick={needsMint ? (e) => {
                    e.preventDefault();
                    setMintError(null);
                    setMinting(s.info_hash!);
                    void Api.library
                      .debridResolve(s.info_hash!, s.file_idx ?? null, s.filename ?? null)
                      .then((r) => { window.location.assign(externalUrl(r.url)); })
                      .catch((err) => {
                        setMinting(null);
                        setMintError((err as Error).message);
                      });
                  } : undefined}
                  className="card flex items-center gap-3 p-3 hover:bg-elev/60 transition-colors group"
                >
                  {minting === s.info_hash && (
                    <span className="text-xs text-parchment/70 shrink-0">resolving…</span>
                  )}
                  <span
                    className="px-2 py-1 rounded text-xs font-display tracking-smallcaps tabular-nums shrink-0"
                    style={{
                      background: qualityColor(s.quality).bg,
                      color: qualityColor(s.quality).fg,
                      border: `1px solid ${qualityColor(s.quality).fg}55`,
                    }}
                  >
                    {s.quality.toUpperCase()}{s.hdr ? " HDR" : ""}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-ivory text-sm truncate">{s.display_title}</div>
                    <div className="text-parchment text-xs mt-0.5 tabular-nums flex gap-3">
                      {s.size_label && <span>{s.size_label}</span>}
                      {s.codec && <span className="text-gold/70">{s.codec}</span>}
                      {s.seeders !== null && <span>👤 {s.seeders}</span>}
                      {s.source && <span className="text-parchment/60">{s.source}</span>}
                    </div>
                  </div>
                  <span className="text-parchment opacity-0 group-hover:opacity-100 transition text-sm">▶</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="text-parchment/50 text-xs mt-3">
        Streams routed through your Real-Debrid account. Resolved via TMDB → IMDb → Torrentio.
      </div>
    </section>
  );
}

function qualityColor(q: string): { fg: string; bg: string } {
  switch (q.toLowerCase()) {
    case "2160p": return { fg: "#E8C977", bg: "rgba(199,162,74,0.15)" };
    case "1080p": return { fg: "#C7A24A", bg: "rgba(199,162,74,0.10)" };
    case "720p":  return { fg: "#A89A7C", bg: "rgba(168,154,124,0.10)" };
    case "480p":  return { fg: "#A89A7C", bg: "rgba(168,154,124,0.06)" };
    default:      return { fg: "#A89A7C", bg: "rgba(168,154,124,0.06)" };
  }
}

function YouTubeFallbackCard({
  item,
}: {
  item: { video_id: string; title: string; channel: string | null; thumbnail_url: string | null };
}): JSX.Element {
  return (
    <li>
      <Link
        to={`/watch/yt/${encodeURIComponent(item.video_id)}`}
        className="card group block overflow-hidden hover:scale-[1.02] transition-transform duration-200 ease-spring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-goldHi"
      >
        <div className="aspect-video bg-elev relative">
          {item.thumbnail_url ? (
            <img
              src={item.thumbnail_url}
              alt=""
              loading="lazy"
              referrerPolicy="no-referrer"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-display text-parchment/40 text-3xl">YT</div>
          )}
          <span
            className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-smallcaps font-display"
            style={{ background: "rgba(15,6,6,0.78)", color: "#E8C977", border: `1px solid #E8C97755` }}
          >
            YouTube
          </span>
        </div>
        <div className="p-3">
          <div className="text-ivory text-sm leading-tight line-clamp-2 min-h-[2.5em]">{item.title}</div>
          <div className="text-parchment text-xs mt-1 truncate">{item.channel ?? "YouTube"}</div>
        </div>
      </Link>
    </li>
  );
}

function kindBadge(kind: string): { label: string; color: string } {
  switch (kind) {
    case "youtube_video": return { label: "YouTube", color: "#E8C977" };
    case "live_channel":  return { label: "Live",    color: "#2C6E5B" };
    case "vod":           return { label: "Movie",   color: "#C7A24A" };
    case "tv_show":       return { label: "TV",      color: "#C42D3A" };
    default:              return { label: kind,      color: "#A89A7C" };
  }
}

function formatDuration(s: number): string {
  if (s < 3600) return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
}
